import { Navigate, Link, Outlet, useLocation } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import { Settings, Users, FileText, MapPin, Globe, Home, LogOut, Mail, LayoutDashboard } from "lucide-react";

const navItems = [
  { label: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Pages", path: "/admin/pages", icon: Globe },
  { label: "Team", path: "/admin/team", icon: Users },
  { label: "Insights", path: "/admin/insights", icon: FileText },
  { label: "Addresses", path: "/admin/addresses", icon: MapPin },
  { label: "Contacts", path: "/admin/contacts", icon: Mail },
  { label: "Settings", path: "/admin/settings", icon: Settings },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAdmin();
  const location = useLocation();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><p className="text-slate-400">Loading...</p></div>;
  if (!admin) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A1628] fixed inset-y-0 left-0 z-40 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-1.5">
            <div className="flex flex-col gap-[3px]">
              <div className="w-4 h-[2px] bg-[#C5A059]"></div>
              <div className="w-4 h-[2px] bg-[#C5A059]"></div>
            </div>
            <span className="font-heading text-white text-base font-medium">Equaliser</span>
            <span className="text-white/50 text-base font-light">Legal</span>
          </Link>
          <p className="text-white/30 text-[10px] uppercase tracking-wider mt-2">Admin Panel</p>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <Link key={item.path} to={item.path} data-testid={`admin-nav-${item.label.toLowerCase()}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-[2px] text-sm transition-colors ${active ? "bg-white/10 text-[#C5A059]" : "text-white/50 hover:text-white hover:bg-white/5"}`}>
                <item.icon size={16} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-xs truncate">{admin.email}</p>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="text-white/40 hover:text-red-400 transition-colors" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
          <Link to="/" className="flex items-center gap-1 text-white/30 text-xs hover:text-[#C5A059] transition-colors mt-2">
            <Home size={12} /> View website
          </Link>
        </div>
      </aside>
      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        <div className="p-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
